/**
 * The brass SPA's credentialed API client (front-door-impl §11.5). All fetches
 * are cookie-based (sb_session) — there is no identity header, mirroring the
 * Agency SPA's authApi. Thin wrappers; the pages own their state machines.
 */

export interface Account {
  id: string;
  email: string;
  displayName?: string;
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── OTP login ──────────────────────────────────────────────────────────────
export type StartResult = { ok: true } | { ok: false; reason: "rate-limited" | "unreachable"; retryAfterMs: number };

export async function startOtp(email: string): Promise<StartResult> {
  try {
    const res = await postJson("/api/login/otp/start", { email });
    if (res.status === 202) return { ok: true };
    if (res.status === 429) {
      const body = (await res.json().catch(() => ({}))) as { retryAfterMs?: number };
      return { ok: false, reason: "rate-limited", retryAfterMs: body.retryAfterMs ?? 60_000 };
    }
    return { ok: false, reason: "unreachable", retryAfterMs: 0 };
  } catch {
    return { ok: false, reason: "unreachable", retryAfterMs: 0 };
  }
}

export type VerifyResult =
  | { ok: true; account: Account }
  | { ok: false; reason: "bad" | "expired" | "locked" | "unreachable" };

export async function verifyOtp(email: string, code: string): Promise<VerifyResult> {
  try {
    const res = await postJson("/api/login/otp/verify", { email, code });
    if (res.ok) {
      const body = (await res.json()) as { account: Account };
      return { ok: true, account: body.account };
    }
    if (res.status === 410) return { ok: false, reason: "expired" };
    if (res.status === 429) return { ok: false, reason: "locked" };
    return { ok: false, reason: "bad" };
  } catch {
    return { ok: false, reason: "unreachable" };
  }
}

// ── session ──────────────────────────────────────────────────────────────
export async function whoami(): Promise<{ account: Account | null }> {
  try {
    const res = await fetch("/api/login/whoami", { credentials: "same-origin" });
    if (!res.ok) return { account: null };
    return (await res.json()) as { account: Account | null };
  } catch {
    return { account: null };
  }
}

export async function logout(): Promise<void> {
  await postJson("/api/login/logout", {}).catch(() => undefined);
}

// ── directory ──────────────────────────────────────────────────────────────
export interface Angel {
  agency_id: string;
  base_url: string;
  label: string;
  linked_at: number;
}

export async function fetchAngels(): Promise<Angel[]> {
  const res = await fetch("/api/whoami", { credentials: "same-origin" });
  if (!res.ok) return [];
  const body = (await res.json()) as { angels: Angel[] };
  return body.angels;
}

// ── passkeys ────────────────────────────────────────────────────────────────
export interface PasskeySummary {
  id: string;
  createdAt: number;
  lastUsed: number | null;
}

export async function fetchPasskeys(): Promise<PasskeySummary[]> {
  const res = await fetch("/api/account/passkeys", { credentials: "same-origin" });
  if (!res.ok) return [];
  const body = (await res.json()) as { passkeys: PasskeySummary[] };
  return body.passkeys;
}

export async function passkeyRegisterStart(): Promise<unknown> {
  const res = await postJson("/api/account/passkey/register/start", {});
  if (!res.ok) throw new Error("could not start passkey registration");
  return res.json();
}

export async function passkeyRegisterFinish(response: unknown): Promise<boolean> {
  const res = await postJson("/api/account/passkey/register/finish", response);
  return res.ok;
}

// ── sessions / sign-out everywhere (§6) ──────────────────────────────────────
export interface SessionSummary {
  id: string;
  current: boolean;
  src: "otp" | "webauthn";
  createdAt: number;
  lastSeen: number;
  expiresAt: number;
}

export async function fetchSessions(): Promise<SessionSummary[]> {
  const res = await fetch("/api/account/sessions", { credentials: "same-origin" });
  if (!res.ok) return [];
  const body = (await res.json()) as { sessions: SessionSummary[] };
  return body.sessions;
}

/** Sign out everywhere. `keepCurrent` spares this device (sign out everywhere else). */
export async function revokeAllSessions(keepCurrent: boolean): Promise<boolean> {
  const res = await postJson("/api/account/sessions/revoke-all", { keepCurrent });
  return res.ok;
}

export async function passkeyAuthStart(): Promise<{ ceremonyId: string; options: unknown }> {
  const res = await postJson("/api/account/passkey/auth/start", {});
  if (!res.ok) throw new Error("could not start passkey login");
  return res.json() as Promise<{ ceremonyId: string; options: unknown }>;
}

export async function passkeyAuthFinish(ceremonyId: string, response: unknown): Promise<VerifyResult> {
  const res = await postJson("/api/account/passkey/auth/finish", { ceremonyId, response });
  if (res.ok) {
    const body = (await res.json()) as { account: Account };
    return { ok: true, account: body.account };
  }
  return { ok: false, reason: "bad" };
}
